/**
 * Rents page: list / search / date filter / pagination / sort via unified
 * GET /api/private/admin/rents (server-side page/pageSize/sort + AND filters).
 * Legacy endpoints remain on backend for transition.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as api from '../../modules/api';
import dayjs from 'dayjs';
import {
  formatRentPlaceLabel,
  getRentCompletionPlace,
  getRentCurrencySymbol,
  getRentPlaceStationNumber,
  getRentStartPlace,
  getRentTariffLabel,
  resolveCompletionStationNumber,
} from '../utils/rentDisplay';
import { normalizePhoneSearchQuery } from '../utils/phoneSearch';
import { formatApiDate, parseApiDate } from '../utils/formatApiDate';
import PageContainer from '../mui-crud/components/PageContainer';
import RentDetailDrawer from './RentDetailDrawer';
import DataGridTableToolbar from '../components/DataGridTableToolbar';
import useDataGridLocaleText from '../hooks/useDataGridLocaleText';

const CROSS_LINK_RENT_ID = 'crossLinkRentId';
const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function rentDateMs(value) {
  const d = parseApiDate(value);
  return d ? d.getTime() : null;
}

function mapRentsToRows(rents) {
  if (!Array.isArray(rents)) return [];
  return rents.map((r, index) => {
    const currency = getRentCurrencySymbol(r);
    const totalCost = r.totalAmount != null ? `${r.totalAmount} ${currency}` : '—';
    const debt = r.debt != null ? `${r.debt} ${currency}` : '0';
    const startPlace = getRentStartPlace(r);
    const completionPlace = getRentCompletionPlace(r);
    const startStationNumber =
      getRentPlaceStationNumber(startPlace, r.stationNumber) ?? '—';
    const completionStationNumber =
      resolveCompletionStationNumber(r) ??
      getRentPlaceStationNumber(
        completionPlace,
        r.completionStationNumber,
      );
    const startLabel = formatRentPlaceLabel(startPlace, r.startStation) ?? '—';
    const completionLabel = formatRentPlaceLabel(completionPlace, r.completionStation, { legacyJoined: true }) ?? '—';
    return {
      id: r.id || `row-${index}`,
      idShort: r.id ? String(r.id).slice(-4) : '—',
      startDate: rentDateMs(r.startDate),
      finishedDate: rentDateMs(r.finishedDate),
      startDateLabel: formatApiDate(r.startDate),
      finishedDateLabel: r.finishedDate ? formatApiDate(r.finishedDate) : null,
      stationNumber: startStationNumber,
      startStation: startLabel,
      completionStationNumber: completionStationNumber ?? '—',
      completionStation: completionLabel ?? '—',
      tariffLabel: getRentTariffLabel(r) ?? '—',
      rentStatus: r.rentStatus ?? '—',
      totalCostDisplay: totalCost,
      debtDisplay: debt,
      _raw: r,
    };
  });
}

/** Classify toolbar query: rentId / powerBank / station serial / station number / phone. */
function classifySearchQuery(query) {
  const str = String(query ?? '').trim();
  if (!str) return {};
  if (/^[0-9a-fA-F]{24}$/.test(str)) {
    return { rentId: str };
  }
  const upper = str.toUpperCase();
  if (/^[A-Z0-9]{16}$/.test(upper) && /[A-Z]/.test(upper)) {
    return { stationId: upper };
  }
  if (/^[A-Z0-9]{12}$/.test(upper) && /[A-Z]/.test(upper)) {
    return { powerBankId: upper };
  }
  if (/^\d{16,}$/.test(str)) {
    return { powerBankId: str };
  }
  const digits = str.replace(/\D/g, '');
  if (digits.length === 4) {
    return { stationNumber: digits };
  }
  if (digits.length >= 4 && digits.length <= 15) {
    const normalized = normalizePhoneSearchQuery(str);
    const phoneDigits = normalized.replace(/\D/g, '');
    return { phone: phoneDigits || digits };
  }
  return { invalid: true };
}

function sortParamFromModel(sortModel) {
  const sort = sortModel?.[0];
  if (!sort?.field) return 'startDate,desc';
  const field = sort.field === 'finishedDate' ? 'finishedDate' : 'startDate';
  const dir = sort.sort === 'asc' ? 'asc' : 'desc';
  return `${field},${dir}`;
}

export default function RentsPage() {
  const { t } = useTranslation();
  const dataGridLocaleText = useDataGridLocaleText();
  const [rows, setRows] = useState([]);
  const [rentsMap, setRentsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rentCount, setRentCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [selectedRent, setSelectedRent] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([{ field: 'startDate', sort: 'desc' }]);
  const openRentIdAfterLoad = useRef(null);
  const requestSeq = useRef(0);

  const applyRows = useCallback((list, total) => {
    const mapped = mapRentsToRows(list);
    setRows(mapped);
    const map = {};
    list.forEach((r) => {
      if (r.id) map[r.id] = r;
    });
    setRentsMap(map);
    setRentCount(Number(total) || list.length);
  }, []);

  const loadRents = useCallback(async ({
    page = 0,
    pageSize = DEFAULT_PAGE_SIZE,
    query = searchQuery,
    range = dateRange,
    status = statusFilter,
    sort = sortModel,
  } = {}) => {
    const seq = ++requestSeq.current;
    setError(null);
    setLoading(true);

    const classified = classifySearchQuery(query);
    if (classified.invalid) {
      setError(t('rents.errorInvalidSearch') || t('rents.errorNotFound'));
      setRows([]);
      setRentsMap({});
      setRentCount(0);
      setLoading(false);
      return;
    }

    try {
      const params = {
        page,
        pageSize,
        sort: sortParamFromModel(sort),
        ...classified,
      };
      if (range?.start && range?.end) {
        params.startDate = dayjs(range.start).startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS');
        params.endDate = dayjs(range.end).endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS');
      }
      if (status) {
        params.status = status;
      }

      const { data } = await api.searchRents(params);
      if (seq !== requestSeq.current) return;

      const list = Array.isArray(data?.rents) ? data.rents : [];
      applyRows(list, data?.rentCount ?? list.length);

      if (openRentIdAfterLoad.current && list.length > 0) {
        const want = openRentIdAfterLoad.current;
        openRentIdAfterLoad.current = null;
        const found = list.find((r) => r.id === want) || list[0];
        setSelectedRent(found);
        setDetailOpen(true);
      }
    } catch (e) {
      if (seq !== requestSeq.current) return;
      const timedOut = e?.code === 'ECONNABORTED' || /timeout/i.test(String(e?.message || ''));
      setError(timedOut ? t('rents.errorTimeout') : (e?.message || t('rents.errorLoad')));
      setRows([]);
      setRentsMap({});
      setRentCount(0);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [applyRows, searchQuery, dateRange, statusFilter, sortModel, t]);

  useEffect(() => {
    loadRents({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      query: searchQuery,
      range: dateRange,
      status: statusFilter,
      sort: sortModel,
    });
  }, [paginationModel.page, paginationModel.pageSize, searchQuery, dateRange, statusFilter, sortModel, loadRents]);

  // Deep-link from other pages.
  useEffect(() => {
    let rentId;
    try {
      rentId = sessionStorage.getItem(CROSS_LINK_RENT_ID);
      if (!rentId) return;
      sessionStorage.removeItem(CROSS_LINK_RENT_ID);
    } catch (_) {
      return;
    }
    openRentIdAfterLoad.current = rentId;
    setSearchInputValue(rentId);
    setSearchQuery(rentId);
    setDateRange(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleRefresh = useCallback(() => {
    if (loading) return;
    loadRents({
      page: paginationModel.page,
      pageSize: paginationModel.pageSize,
      query: searchQuery,
      range: dateRange,
      status: statusFilter,
      sort: sortModel,
    });
  }, [loading, loadRents, paginationModel, searchQuery, dateRange, statusFilter, sortModel]);

  const handleSearch = useCallback(
    (query) => {
      const q = (query ?? searchInputValue ?? '').toString().trim();
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      if (!q) {
        setSearchQuery(null);
        setSearchInputValue('');
        return;
      }
      setSearchQuery(q);
      setSearchInputValue(q);
    },
    [searchInputValue],
  );

  const handleClearSearch = useCallback(() => {
    setSearchQuery(null);
    setSearchInputValue('');
    setError(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleDateFilter = useCallback((data) => {
    const start = data?.StartDate;
    const end = data?.EndDate;
    if (!start || !end) return;
    setDateRange({ start, end });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleClearDate = useCallback(() => {
    setDateRange(null);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleStatusFilterChange = useCallback((status) => {
    const next = status || null;
    setStatusFilter(next);
    setFilterModel((prev) => {
      const otherItems = (prev?.items || []).filter((item) => item.field !== 'rentStatus');
      if (!next) return { ...prev, items: otherItems };
      return {
        ...prev,
        items: [
          ...otherItems,
          { id: 'rentStatus', field: 'rentStatus', operator: 'is', value: next },
        ],
      };
    });
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleClearStatus = useCallback(() => {
    handleStatusFilterChange(null);
  }, [handleStatusFilterChange]);

  const handleFilterModelChange = useCallback((model) => {
    setFilterModel(model || { items: [] });
    const statusItem = (model?.items || []).find(
      (item) => item.field === 'rentStatus' && item.value != null && item.value !== '',
    );
    const nextStatus = statusItem?.value ? String(statusItem.value) : null;
    setStatusFilter((prev) => (prev === nextStatus ? prev : nextStatus));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handlePaginationModelChange = useCallback((model) => {
    setPaginationModel({
      page: model.page || 0,
      pageSize: model.pageSize || DEFAULT_PAGE_SIZE,
    });
  }, []);

  const handleSortModelChange = useCallback((model) => {
    setSortModel(model?.length ? model : [{ field: 'startDate', sort: 'desc' }]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handleRowClick = useCallback(
    (params) => {
      const full = rentsMap[params.row.id] || params.row._raw || params.row;
      setSelectedRent(full);
      setDetailOpen(true);
    },
    [rentsMap],
  );

  const columns = useMemo(
    () => [
      { field: 'idShort', headerName: t('rents.columnId'), flex: 0.4, minWidth: 60, sortable: false },
      {
        field: 'startDate',
        headerName: t('rents.columnStart'),
        flex: 0.8,
        minWidth: 120,
        type: 'number',
        valueFormatter: (value, row) => row?.startDateLabel || formatApiDate(row?._raw?.startDate),
        renderCell: (params) => params.row.startDateLabel || '—',
      },
      {
        field: 'finishedDate',
        headerName: t('rents.columnEnd'),
        flex: 0.8,
        minWidth: 120,
        type: 'number',
        renderCell: (params) =>
          params.row.finishedDateLabel || t('rents.statusInProgress'),
      },
      { field: 'stationNumber', headerName: t('rents.columnStation'), flex: 0.5, minWidth: 80, sortable: false },
      { field: 'startStation', headerName: t('rents.columnStartStation'), flex: 1, minWidth: 140, sortable: false },
      { field: 'completionStationNumber', headerName: t('rents.columnEndStationNumber'), flex: 0.5, minWidth: 80, sortable: false },
      { field: 'completionStation', headerName: t('rents.columnEndStation'), flex: 1, minWidth: 140, sortable: false },
      { field: 'tariffLabel', headerName: t('rents.columnTariff'), flex: 0.8, minWidth: 120, sortable: false },
      { field: 'totalCostDisplay', headerName: t('rents.columnTotalCost'), flex: 0.5, minWidth: 90, sortable: false },
      { field: 'debtDisplay', headerName: t('rents.columnDebt'), flex: 0.5, minWidth: 90, sortable: false },
      {
        field: 'rentStatus',
        headerName: t('rents.columnStatus'),
        flex: 0.5,
        minWidth: 90,
        sortable: false,
        type: 'singleSelect',
        valueOptions: [
          { value: 'FINISHED', label: t('rents.statusFinished') },
          { value: 'FAILED', label: t('rents.statusFailed') },
          { value: 'PROCESSING', label: t('rents.statusInProgress') },
        ],
        renderCell: (params) => {
          const status = params.value;
          const label =
            status === 'FINISHED' ? t('rents.statusFinished') : status === 'FAILED' ? t('rents.statusFailed') : status === 'PROCESSING' ? t('rents.statusInProgress') : status;
          const color = status === 'FINISHED' ? 'default' : status === 'FAILED' ? 'error' : 'success';
          return <Chip label={label} color={color} size="small" />;
        },
      },
    ],
    [t],
  );

  const dateChipLabel = dateRange
    ? `${dayjs(dateRange.start).format('DD.MM.YYYY')} – ${dayjs(dateRange.end).format('DD.MM.YYYY')}`
    : null;

  const statusChipLabel = statusFilter
    ? (statusFilter === 'FINISHED'
      ? t('rents.statusFinished')
      : statusFilter === 'FAILED'
        ? t('rents.statusFailed')
        : t('rents.statusInProgress'))
    : null;

  return (
    <Box sx={{ width: '100%' }}>
      <PageContainer
        title={t('rents.title')}
        breadcrumbs={[]}
        actions={
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
            {searchQuery != null && (
              <Chip
                label={`${t('common.search')}: ${searchQuery}`}
                onDelete={handleClearSearch}
                size="small"
              />
            )}
            {dateChipLabel && (
              <Chip
                label={dateChipLabel}
                onDelete={handleClearDate}
                size="small"
              />
            )}
            {statusChipLabel && (
              <Chip
                label={`${t('rents.filterStatus')}: ${statusChipLabel}`}
                onDelete={handleClearStatus}
                size="small"
              />
            )}
            <Tooltip title={t('common.reloadData')} placement="right" enterDelay={1000}>
              <div>
                <IconButton size="small" aria-label="refresh" onClick={handleRefresh}>
                  <RefreshIcon />
                </IconButton>
              </div>
            </Tooltip>
          </Stack>
        }
      >
        <Box sx={{ flex: 1, width: '100%', minHeight: 520 }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          ) : null}
          <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              rowCount={rentCount}
              pagination
              paginationMode="server"
              sortingMode="server"
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onRowClick={handleRowClick}
              localeText={dataGridLocaleText}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              disableColumnResize
              density="compact"
              showToolbar
              slots={{ toolbar: DataGridTableToolbar }}
              slotProps={{
                pagination: {
                  labelRowsPerPage: t('common.rowsPerPage'),
                },
                toolbar: {
                  searchValue: searchInputValue,
                  onSearchChange: setSearchInputValue,
                  onSearchSubmit: handleSearch,
                  searchPlaceholder: t('rents.searchPlaceholder'),
                  rentDateFilter: true,
                  onRentDateFilterApply: handleDateFilter,
                  loading,
                },
                filterPanel: {
                  filterFormProps: {
                    logicOperatorInputProps: { variant: 'outlined', size: 'small' },
                    columnInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                    operatorInputProps: { variant: 'outlined', size: 'small', sx: { mt: 'auto' } },
                    valueInputProps: { InputComponentProps: { variant: 'outlined', size: 'small' } },
                  },
                },
                loadingOverlay: { variant: 'circular-progress', noRowsVariant: 'circular-progress' },
                baseIconButton: { size: 'small' },
              }}
              sx={{
                minHeight: 480,
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: { outline: 'transparent' },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]: { outline: 'none' },
                [`& .${gridClasses.row}`]: { cursor: 'pointer' },
              }}
            />
        </Box>
      </PageContainer>
      <RentDetailDrawer
        rent={selectedRent}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setSelectedRent(null); }}
        onRefresh={handleRefresh}
      />
    </Box>
  );
}
